import ModalConfirmGroup, { IStaffAssignItem, useModalConfirmGroupRef } from 'AppointmentBox/ModalConfirmGroup';
import { ANYONE } from 'Calendar/anyone';
import { Form } from 'antd';
import Box from 'components/Box';
import { loadingSignal } from 'components/Loading';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import { QUICK_BOOKING_INDEX } from 'constants/variables';
import { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import aptActions from 'services/actions/apt';
import shopSelectors from 'services/selectors/shop';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import toast from 'utils/toast';
import StaffAvailablePicker from 'widgets/Staff/StaffAvailablePicker';
import CustomerQuickBookingSection from './CustomerSection';
import GroupNumberSection from './GroupNumberSection';
import InputNote from './InputNote';
import RepeatSection, { canRepeatForStaff } from './RepeatSection';
import Modal from './Modal';
import StaffServicesSection from './StaffServicesSection';
import TimeSection from './Time';
import qbSignal, { IQuickBookRef, IQuickBookStaffSelected } from './signal';
import { validateQuickBookingRows } from './quick-booking-validation';
import { buildQuickBookingGroupItems } from './quick-booking-group-payload';
import iconPlus from './icon-plus.svg';
import { isBookableStaff } from 'utils/staff';
import { confirmSameDayBooking } from 'widgets/SameDayBookingAlert/confirm-same-day-booking';
import { isBookingEnhance } from 'services/booking-context';

const QuickBookingBox = forwardRef<IQuickBookRef>((_, ref) => {
  const dispatch = useAppDispatch();
  const [formStaff] = Form.useForm();
  const _staffStoreAll = shopSelectors.staff();
  // Memoised so the filtered array keeps a stable identity across renders.
  const _staffStore = useMemo(() => _staffStoreAll.filter(isBookableStaff), [_staffStoreAll]);
  const modalConfirmGroupRef = useModalConfirmGroupRef();
  /**
   * Dong staff da mo buoc gan tho. Giu lai vi modal Quick Booking dong khi buoc do mo, va
   * lich chi duoc dung sau khi nguoi dung bam xong o modal gan tho.
   */
  const groupRowRef = useRef<IQuickBookStaffSelected | null>(null);
  // Tiem bat cau hinh nay thi so khach >= 2 chuyen sang buoc gan tho, moi khach mot lich rieng.
  const groupAssignEnabled = shopSelectors.setting().quickBookingGroupAssign;
  const handleClose = () => qbSignal.close();
  const handleBlockHours = async () => {
    try {
      await formStaff.validateFields();
    } catch (error) {
      toast.error('Please fill in all fields');
      return;
    }

    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');

    // Modal co the mo voi danh sach staff rong (luong FULL_SCREEN), nen phai chan tu day:
    // dat lich khong staff hoac staff khong dich vu deu tao ra lich vo nghia phia server.
    const validation = validateQuickBookingRows(staffsSelected);
    if (!validation.ok) {
      if (validation.reason === 'NO_STAFF') {
        toast.info('Please add at least one staff');
        return;
      }
      toast.info('Please select at least one service');
      qbSignal.serviceWarningRow.value = validation.rowIndex;
      return;
    }

    // Nhom tu 2 khach tro len khong dat ngay: moi khach can mot nguoi lam rieng, nen phai
    // qua buoc gan tho truoc. Form o che do nay chi cho mot dong staff.
    if (groupAssignEnabled && qbSignal.totalPeople.value >= 2) {
      openGroupAssign(staffsSelected[0]);
      return;
    }

    const baseValues = qbSignal.getValues(staffsSelected);

    // Booking-enhance: hide the form at once (values kept) while the same-day lookup runs; a
    // conflict or a cancel brings it back, a proceed lets the saga close it (same as AppointmentBox).
    const enhance = isBookingEnhance();
    const showForm = () => { qbSignal.openSignal.value = true; };
    if (enhance) qbSignal.openSignal.value = false;
    // Khach da co lich trong ngay → canh bao + PIN staff. PIN do duoc gui kem de biet ai dat.
    const sameDay = await confirmSameDayBooking({
      customerId: baseValues[0]?.customerId,
      startTime: baseValues[0]?.startTime || '',
      totalPeople: qbSignal.totalPeople.value,
      silent: enhance,
      onConflict: enhance ? showForm : undefined,
    });
    if (sameDay.status === 'cancel') {
      if (enhance) showForm();
      return;
    }
    const actor = sameDay.actor;
    const values = actor ? baseValues.map(o => ({ ...o, ...actor })) : baseValues;

    const dayRepeat = qbSignal.repeatDayRepeat.value;
    if (!dayRepeat) {
      dispatch(aptActions.quickBooking.doQuickBooking({ items: values }));
      return;
    }

    // Staff vua nhap PIN o buoc canh bao → dung lai PIN do cho repeat, khong hoi lan hai.
    if (actor) {
      dispatch(aptActions.quickBooking.doQuickBooking({
        items: values,
        repeat: { dayRepeat, pinCode: actor.pinCode },
      }));
      return;
    }

    // Tao chuoi la thao tac ghi lon (toi 12 lich) nen di kem PIN nhan vien, cung muc voi
    // update/delete/cancel. Dat lich thuong khong doi PIN — chi hoi khi co chon repeat.
    verifyPinCode.current?.checkStaffPin((pinCode) => {
      dispatch(aptActions.quickBooking.doQuickBooking({
        items: values,
        repeat: { dayRepeat, pinCode: pinCode || '' },
      }));
    });
  };

  /**
   * Chuyen sang buoc gan tho: nap danh sach tho ranh trong dung khung gio dang chon roi
   * nhuong man hinh cho modal gan tho.
   */
  const openGroupAssign = (row: IQuickBookStaffSelected) => {
    dispatch(aptActions.staffAvailableBooking.fetch(qbSignal.getCurrentStartEndTime()));
    groupRowRef.current = row;
    const source = qbSignal.getGroupSource(row);
    const totalPeople = qbSignal.totalPeople.value;
    qbSignal.close();
    modalConfirmGroupRef.current?.open(totalPeople, source);
  };

  const handleGroupDone = (staffAssigns: IStaffAssignItem[]) => {
    const row = groupRowRef.current;
    if (!row || !staffAssigns?.length) return;
    const items = buildQuickBookingGroupItems(qbSignal.getGroupState(row), staffAssigns);
    dispatch(aptActions.quickBooking.doQuickBooking({ items }));
  };

  /** BACK o buoc gan tho: tra lai form Quick Booking nguyen ven de sua tiep. */
  const handleGroupCancel = () => {
    qbSignal.openSignal.value = true;
  };

  const staffStore = useMemo(() => [ANYONE].concat(_staffStore), [_staffStore]);

  const handleAddMoreStaff = (add: (options: Partial<IQuickBookStaffSelected>) => void) => async () => {
    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');
    const queryData = qbSignal.getCurrentStartEndTime();
    const currentStaff = await StaffAvailablePicker.getAvailableWithTime(queryData, staffsSelected?.map(o => o.id?.toString() || '') || []);
    if (!currentStaff) return;
    add({
      id: currentStaff.id,
      services: [],
      requestStaff: qbSignal.requestStaff.value,
    });
  };

  const handleOnChangeStaff = async (value?: IQuickBookStaffSelected, onChange?: (value: IQuickBookStaffSelected) => void) => {
    const staffsSelected: IQuickBookStaffSelected[] = formStaff.getFieldValue('staffsSelected');
    const queryData = qbSignal.getCurrentStartEndTime();
    const currentStaff = await StaffAvailablePicker.getAvailableWithTime(queryData, staffsSelected?.map(o => o.id?.toString() || '') || []);
    if (!currentStaff) return;
    onChange?.({
      ...value || {},
      id: currentStaff.id,
    } as IQuickBookStaffSelected);
  };


  /**
   * Form la nguon su that cho danh sach staff; `qbSignal.staffs` chi la ban chieu cho
   * TimeSection / RepeatSection doc. Khong dong bo thi modal mo voi staff rong se ket o trang
   * thai "chua co staff" ke ca sau khi user da them.
   */
  const syncStaffsSignal = () => {
    const ids = ((formStaff.getFieldValue('staffsSelected') ?? []) as IQuickBookStaffSelected[])
      .map(row => row?.id)
      .filter(Boolean) as string[];
    // So sanh truoc khi ghi de khong ban ra render moi lan user go ten dich vu.
    if (ids.join(',') !== qbSignal.staffs.value.join(',')) {
      qbSignal.staffs.value = ids;
      // Canh bao thieu dich vu tro toi dong theo chi so; them / xoa / doi staff lam cac dong
      // duoi bi danh so lai, nen chi so cu se tro nham dong khac.
      qbSignal.serviceWarningRow.value = null;
      // Section REPEAT bien mat khi staff dau danh sach khong con la staff cu the. Gia tri lap
      // phai di theo no, neu khong booking van gui payload lap ma user khong con control de go.
      if (!canRepeatForStaff(ids[0])) qbSignal.repeatDayRepeat.value = null;
    }
  };

  useImperativeHandle(ref, () => ({
    initFormService: (data: IQuickBookStaffSelected[]) => {
      formStaff.setFieldsValue({
        staffsSelected: data,
      });
    }
  }));

  return (
    <>
      <Modal
        title="QUICK BOOKING"
        open={qbSignal.openSignal.value}
        onCancel={handleClose}
        okText={'Booking'}
        onOk={handleBlockHours}
        height='auto'
        width='90vw'
        loading={loadingSignal.value}
        style={{ zoom: 0.8 }}
        visibleClose
        zIndex={QUICK_BOOKING_INDEX}
      >
        <FormStyled>
          <div className='customer-staff'>
            <CustomerQuickBookingSection />
            <GroupNumberSection />
          </div>
          <TimeSection />
          <InputNote />
          <RepeatSection staffId={qbSignal.staffs.value?.[0]} />
          <Form form={formStaff} onValuesChange={syncStaffsSignal}>
            <Form.List name={'staffsSelected'}>
              {(fields, { add, remove }) => {
                return <Box className='section-wrapper' display='flex' flexDirection='column' gap='4'>
                  <Box display='flex' flexDirection='column' gap='6'>
                    {fields.map(field => (
                      <Form.Item key={field.key} name={field.name} noStyle>
                        <StaffServicesSection
                          handleOnChangeStaff={handleOnChangeStaff}
                          disabledRemove={(fields.length || 0) <= 1}
                          remove={() => remove(field.name)}
                          name={field.name}
                          staffStore={staffStore}
                        />
                      </Form.Item>
                    ))}
                  </Box>
                  {/* Che do gan tho chi lam viec voi mot dong staff: dich vu va Request Staff
                      cua ca nhom deu lay tu dong do. Van cho them dong dau tien vi modal co the
                      mo voi danh sach rong (luong FULL_SCREEN). */}
                  {(!groupAssignEnabled || fields.length < 1) && <Box display='flex' alignItems='center' justifyContent='start'>
                    <button onClick={handleAddMoreStaff(add)} type='button' className='btn-add-more'>
                      <img src={iconPlus} alt='icon-plus' />
                      <span>{fields.length ? 'Add More Staff' : 'Add Staff'}</span>
                    </button>
                  </Box>}
                </Box>;
              }}
            </Form.List>
          </Form>
        </FormStyled>
      </Modal>
      {/* Cung tang voi modal Quick Booking: khi mo tu ngan keo Caller ID, muc mac dinh cua
          antd nam duoi ngan keo do. */}
      <ModalConfirmGroup
        ref={modalConfirmGroupRef}
        onOk={handleGroupDone}
        onCancel={handleGroupCancel}
        zIndex={QUICK_BOOKING_INDEX}
      />
    </>
  );
});

export default QuickBookingBox;

const FormStyled = styled.div`
padding: 1rem 1.5rem;
flex: 1;
gap: 8px;
display: flex;
flex-direction: column;
  .section-wrapper {
    background: #fff;
    border-radius: 8px;
    padding: 8px 16px;
    gap: 8px;
    flex: 1 0 0;
  }

  .customer-staff {
    display: flex;
    gap: 24px;
    .customer-staff-item {
      flex: 1;
    }
  }
  .section-label {
   color: #7B7B7B;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 120% */
    &.mb {
    margin-bottom: 8px;
    }
  }

  .btn-add-more {
    display: flex;
    height: 48px;
    padding: 0px 8px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 4px;
    background: #148CF1;
    box-shadow: 0 4px 8px -3px rgba(45, 35, 66, 0.30);
    span {
      color: #FFF;
      text-align: center;
      font-family: Poppins;
      font-size: 16px;
      font-style: normal;
      font-weight: 600;
      line-height: normal;
    }
  }
`;